import React from "react";
import MyAccessRequestTable from "components/AccessRequestTable/MyAccessRequestTable";
import Accordion from "components/odl-v2/Accordion/Accordion";
import AccordionDetails from "components/odl-v2/Accordion/AccordionDetails";
import AccordionSummary from "components/odl-v2/Accordion/AccordionSummary";
import Spacer from "components/Spacer/Spacer";
import AccessRequestConfirmationDialog from "components/AccessRequestConfirmationDialog/AccessRequestConfirmationDialog";
import CountBadge from "components/CountBadge/CountBadge";
import { css } from "@emotion/react";
import { duration, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDetectSticky } from "./functions/useDetectSticky";
import {
  fetchAccessRequests,
  generateStorageKey,
  selectRecentMyAccessRequests,
} from "store/domain-data/access-request/accessRequest";
import { useStoreDispatch, useStoreSelector } from "store/hooks";
import { useAccessRequestActions } from "hooks/useAccessRequestActions";
import { useToast } from "hooks/useToast";
import { serviceContainer } from "services/serviceContainer";

type Props = {};

const MyAccessRequestAccordion: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { toastError } = useToast();
  const dispatch = useStoreDispatch();

  const [isExpanded, setIsExpanded] = React.useState(true);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = React.useState(false);

  const summaryRef = React.useRef<HTMLDivElement>(null);
  const detailsRef = React.useRef<HTMLDivElement>(null);

  const { openApplication, requestAccess } = useAccessRequestActions();
  const isSticky = useDetectSticky(summaryRef);
  const myAccessRequests = useStoreSelector(selectRecentMyAccessRequests);
  const myAccessRequestCount = myAccessRequests.length || 0;

  const handleChangeExpanded = React.useCallback(
    async (event: React.SyntheticEvent, expanded: boolean) => {
      setIsExpanded(expanded);

      // Wait until the accordion has expanded
      if (expanded && isSticky) {
        setTimeout(() => {
          detailsRef.current?.scrollIntoView({
            behavior: "smooth",
          });
        }, duration.standard);
      }
    },
    [isSticky]
  );

  const accessRequestConfirmationDialog = React.useMemo(() => {
    if (!isConfirmationDialogOpen) {
      return null;
    }
    return <AccessRequestConfirmationDialog onClose={() => setIsConfirmationDialogOpen(false)} />;
  }, [isConfirmationDialogOpen]);

  const handleClickRequestAccess = React.useCallback(
    async (applicationId: number) => {
      const logger = serviceContainer.cradle.logger;
      try {
        await requestAccess(applicationId);
        setIsConfirmationDialogOpen(true);
      } catch (e) {
        logger.error("Participant access request submit failed", e);
        toastError(e);
      }
    },
    [requestAccess, setIsConfirmationDialogOpen, toastError]
  );

  const handleClickOpenApplication = React.useCallback(
    async (accessRequestGuid: string, applicationId: number) => {
      const myAccessRequest = myAccessRequests.find((accessRequest) => accessRequest.requestGuid === accessRequestGuid);
      if (!myAccessRequest) {
        return;
      }

      localStorage.setItem(generateStorageKey(accessRequestGuid), "true");

      await dispatch(fetchAccessRequests());
      await openApplication(applicationId, myAccessRequest.applicationStatus);
    },
    [openApplication, dispatch, myAccessRequests]
  );

  return (
    <StyledAccordion expanded={isExpanded} onChange={handleChangeExpanded} $isSticky={isSticky}>
      <AccordionSummary ref={summaryRef}>
        <Typography variant="h3">{t(`Your access requests`)}</Typography>
        <Spacer x={2} />
        <CountBadge count={myAccessRequestCount} data-testid={`Adornment-${myAccessRequestCount}`} />
      </AccordionSummary>
      <AccordionDetails ref={detailsRef}>
        <MyAccessRequestTable
          data={myAccessRequests}
          onClickOpenApplication={handleClickOpenApplication}
          onClickRequestAccess={handleClickRequestAccess}
        />
      </AccordionDetails>
      {accessRequestConfirmationDialog}
    </StyledAccordion>
  );
};

const StyledAccordion = styled(Accordion, { shouldForwardProp: (prop) => prop !== "$isSticky" })<{
  $isSticky?: boolean;
}>(
  ({ theme, expanded, $isSticky = false }) => css`
    display: contents;

    .MuiAccordionSummary-root {
      min-height: 56px !important;
      height: 56px;
      position: sticky;
      bottom: -1px;
      z-index: 1;
      background-color: ${theme.palette.background.paper};
      border-top: 1px solid ${theme.palette.grey[500]};

      ${!$isSticky &&
      css`
        border-top-left-radius: ${theme.spacing(2)};
        border-top-right-radius: ${theme.spacing(2)};
        border-top: none;
      `}

      ${!expanded &&
      css`
        border-bottom-left-radius: ${theme.spacing(2)};
        border-bottom-right-radius: ${theme.spacing(2)};
      `}

      & > .MuiAccordionSummary-content {
        min-height: 56px !important;
      }
    }

    .MuiAccordionDetails-root {
      padding: 0;
      overflow: hidden;
      border-bottom-left-radius: ${theme.spacing(2)};
      border-bottom-right-radius: ${theme.spacing(2)};
    }
  `
);

export default MyAccessRequestAccordion;
